import { apiClient } from './apiClient';

const BASE_URL = '/emails';
const EMAIL_AGENT_BASE_URL = '/email-agent';
const INBOX_BASE_URL = '/inbox'; // NEW: For the native inbox experience

/**
 * Email service for API operations
 * Handles all email-related operations including listing, filtering, and retrieving content
 */
export class EmailService {
  /**
   * List emails with optional filters.
   * @param {Object} params - Query parameters: from, subject, date, etc.
   * @returns {Promise<Array>} Array of email objects
   */
  async listEmails(params = {}) {
    try {
      const response = await apiClient.get(`${BASE_URL}`, { params });
      return response
    } catch (error) {
      console.error('Error fetching emails:', error);
      throw error;
    }
  }

  // --- NEW INBOX API METHODS ---

  /**
   * List inbox messages (paginated)
   * @param {Object} params - Query parameters: query (search), pageToken, maxResults
   * @returns {Promise<Object>} { messages, nextPageToken }
   */
  async listInboxMessages(params = {}) {
    try {
      const response = await apiClient.get(`${INBOX_BASE_URL}/messages`, { params });
      return response;
    } catch (error) {
      console.error('Error fetching inbox messages:', error);
      throw error;
    }
  }

  /**
   * Get single inbox message details
   * @param {string} messageId 
   * @returns {Promise<Object>}
   */
  async getInboxMessage(messageId) {
    try {
      const response = await apiClient.get(`${INBOX_BASE_URL}/messages/${messageId}`);
      return response;
    } catch (error) {
      console.error('Error fetching inbox message details:', error);
      throw error;
    }
  }

  /**
   * Get AI run summary for a message
   * @param {string} messageId 
   * @returns {Promise<Object>}
   */
  async getAiRunSummary(messageId) {
    try {
      const response = await apiClient.get(`${INBOX_BASE_URL}/messages/${messageId}/ai-summary`);
      return response;
    } catch (error) {
      console.error('Error fetching AI run summary:', error);
      throw error;
    }
  }

  /**
   * Mark message as read
   * @param {string} messageId 
   */
  async markAsRead(messageId) {
    try {
      const response = await apiClient.patch(`${INBOX_BASE_URL}/messages/${messageId}/read`);
      return response;
    } catch (error) {
      console.error('Error marking message as read:', error);
      throw error;
    }
  }

  /**
   * Get full thread for a message
   * @param {string} threadId 
   * @returns {Promise<Array>}
   */
  async getThread(threadId) {
    try {
      const response = await apiClient.get(`${INBOX_BASE_URL}/threads/${threadId}`);
      return response;
    } catch (error) {
      console.error('Error fetching thread:', error);
      throw error;
    }
  }

  /**
   * Search Gmail threads (paginated)
   * @param {Object} params - Query parameters: query (search), pageToken, maxResults
   * @returns {Promise<Object>} { threads, nextPageToken }
   */
  async searchThreads(params = {}) {
    try {
      const response = await apiClient.get(`${INBOX_BASE_URL}/threads/search`, { params });
      return response;
    } catch (error) {
      console.error('Error searching Gmail threads:', error);
      throw error;
    }
  }

  /**
   * List sent messages (paginated)
   * @param {Object} params - Query parameters: pageToken, maxResults
   * @returns {Promise<Object>} { messages, nextPageToken }
   */
  async listSentMessages(params = {}) {
    try {
      const response = await apiClient.get(`${INBOX_BASE_URL}/messages/sent`, { params });
      return response;
    } catch (error) {
      console.error('Error fetching sent messages:', error);
      throw error;
    }
  }

  /**
   * Analyze an email for context/prefill data
   * @param {string} messageId 
   * @param {string} type - e.g. 'case', 'note'
   * @returns {Promise<Object>}
   */
  async analyzeMessage(messageId, type) {
    try {
      const response = await apiClient.post(`${INBOX_BASE_URL}/messages/${messageId}/analyze`, { type });
      return response;
    } catch (error) {
      console.error('Error analyzing inbox message:', error);
      throw error;
    }
  }

  async startAnalyzeMessageRun(messageId, type) {
    try {
      const response = await apiClient.post(`${INBOX_BASE_URL}/messages/${messageId}/analyze/start`, { type });
      return response;
    } catch (error) {
      console.error('Error starting inbox analyze run:', error);
      throw error;
    }
  }

  async getAnalyzeRun(runId) {
    try {
      const response = await apiClient.get(`${INBOX_BASE_URL}/analyze-runs/${runId}`);
      return response;
    } catch (error) {
      console.error('Error fetching inbox analyze run:', error);
      throw error;
    }
  }

  async gapAnalysis(messageId) {
    try {
      const response = await apiClient.post(`${INBOX_BASE_URL}/messages/${messageId}/gap-analysis`);
      return response;
    } catch (error) {
      console.error('Error running gap analysis:', error);
      throw error;
    }
  }

  async saveCase(messageId, formState) { // Changed claimData to formState to match backend
    try {
      const response = await apiClient.post(`${INBOX_BASE_URL}/actions/save-case`, { messageId, formState });
      return response;
    } catch (error) {
      console.error('Error saving case from inbox:', error);
      throw error;
    }
  }

  async saveWorkorder(messageId, formState) {
    try {
      const response = await apiClient.post(`${INBOX_BASE_URL}/actions/save-workorder`, { messageId, formState });
      return response;
    } catch (error) {
      console.error('Error saving workorder from inbox:', error);
      throw error;
    }
  }

  async saveEvent(messageId, formState) {
    try {
      const response = await apiClient.post(`${INBOX_BASE_URL}/actions/save-event`, { messageId, formState });
      return response;
    } catch (error) {
      console.error('Error saving event from inbox:', error);
      throw error;
    }
  }

  async saveNote(messageId, formState, context) {
    try {
      const response = await apiClient.post(`${INBOX_BASE_URL}/actions/save-note`, { messageId, formState, context });
      return response;
    } catch (error) {
      console.error('Error saving note from inbox:', error);
      throw error;
    }
  }

  async ingestAttachmentToClaim(messageId, attachmentId, claimId, sendEfax = false, recipientFax = null) {
    try {
      const response = await apiClient.post(`${INBOX_BASE_URL}/actions/ingest-attachment`, {
        messageId,
        attachmentId,
        claimId,
        sendEfax,
        recipientFax,
      });
      return response;
    } catch (error) {
      console.error('Error ingesting attachment to claim:', error);
      throw error;
    }
  }

  async extractNotes(messageIds) {
    try {
      const response = await apiClient.post(`${INBOX_BASE_URL}/actions/extract-notes`, { messageIds });
      return response;
    } catch (error) {
      console.error('Error extracting notes from inbox:', error);
      throw error;
    }
  }

  async askAiQuestion(messageIds, question, options = {}) {
    try {
      const response = await apiClient.post(`${INBOX_BASE_URL}/actions/ask-ai`, {
        messageIds,
        question,
        researchMode: options?.researchMode || 'quick',
        allowExternalResearch: Boolean(options?.allowExternalResearch),
      });
      return response;
    } catch (error) {
      console.error('Error asking AI from inbox:', error);
      throw error;
    }
  }

  async startAiWorkItem(type, payload = {}, options = {}) {
    try {
      const response = await apiClient.post(`${INBOX_BASE_URL}/ai-work-items/start`, {
        type,
        payload,
        title: options?.title,
        idempotencyKey: options?.idempotencyKey,
        forceNewRun: Boolean(options?.forceNewRun),
        source: options?.source || 'inbox',
      });
      return response;
    } catch (error) {
      console.error('Error starting inbox AI work item:', error);
      throw error;
    }
  }

  async listAiWorkItems(params = {}) {
    try {
      const response = await apiClient.get(`${INBOX_BASE_URL}/ai-work-items`, { params });
      return response;
    } catch (error) {
      console.error('Error listing inbox AI work items:', error);
      throw error;
    }
  }

  async getAiWorkItem(workId) {
    try {
      const response = await apiClient.get(`${INBOX_BASE_URL}/ai-work-items/${workId}`);
      return response;
    } catch (error) {
      console.error('Error fetching inbox AI work item:', error);
      throw error;
    }
  }

  async retryAiWorkItem(workId) {
    try {
      const response = await apiClient.post(`${INBOX_BASE_URL}/ai-work-items/${workId}/retry`, {});
      return response;
    } catch (error) {
      console.error('Error retrying inbox AI work item:', error);
      throw error;
    }
  }

  async deleteAiWorkItem(workId) {
    try {
      const response = await apiClient.delete(`${INBOX_BASE_URL}/ai-work-items/${workId}`);
      return response;
    } catch (error) {
      console.error('Error deleting inbox AI work item:', error);
      throw error;
    }
  }

  async sendReply(messageId, replyData) {
    try {
      const response = await apiClient.post(`${INBOX_BASE_URL}/actions/send-reply`, { messageId, replyData });
      return response;
    } catch (error) {
      console.error('Error sending reply from inbox:', error);
      throw error;
    }
  }

  /**
   * Compose a new email
   * @param {Object} data - { to, cc, bcc, subject, message, replyToMessageId, threadId, attachments }
   * Note: attachments can be either File objects (new uploads) or already-uploaded attachment metadata
   */
  async composeEmail(data) {
    try {
      const { attachments, ...emailData } = data;
      
      let processedAttachments = undefined;
      if (attachments && attachments.length > 0) {
        processedAttachments = [];
        
        for (const att of attachments) {
          // Check if it's already uploaded (has gcsPath) or is a new File object
          if (att.gcsPath) {
            // Already uploaded - use the metadata directly
            processedAttachments.push({
              name: att.name,
              gcsPath: att.gcsPath,
              mimeType: att.mimeType
            });
          } else if (att.sourceMessageId && (att.attachmentId || att.url)) {
            // Forwarded inbox attachment metadata. The backend resolves the Gmail content.
            processedAttachments.push({
              name: att.name || att.filename,
              mimeType: att.mimeType || att.contentType,
              sourceMessageId: att.sourceMessageId,
              attachmentId: att.attachmentId || att.url,
            });
          } else if (att instanceof File) {
            // New file - upload it first
            const formData = new FormData();
            console.log('Uploading file:', att.name, att.type, att.size);
            formData.append('file', att);
            
            const uploadResponse = await apiClient.upload(`/messages/upload-attachment`, formData);
            console.log('Upload response:', uploadResponse);
            
            if (uploadResponse?.success && uploadResponse.data) {
              processedAttachments.push({
                name: uploadResponse.data.name,
                gcsPath: uploadResponse.data.gcsPath,
                mimeType: uploadResponse.data.mimeType
              });
            }
          } else if (att && typeof att === 'object') {
            // Pass through any other attachment metadata we already have.
            processedAttachments.push({
              ...att,
              name: att.name || att.filename,
              mimeType: att.mimeType || att.contentType,
            });
          }
        }
      }

      const response = await apiClient.post(`${INBOX_BASE_URL}/actions/compose`, {
        ...emailData,
        attachments: processedAttachments
      });
      return response;
    } catch (error) {
      console.error('Error composing email:', error);
      throw error;
    }
  }

  /**
   * Get action status (forwarded/faxed) for a list of message IDs
   * @param {Array<string>} messageIds
   */
  async getMessagesActionsStatus(messageIds) {
    try {
      const response = await apiClient.post(`${INBOX_BASE_URL}/messages/actions-status`, { messageIds });
      return response;
    } catch (error) {
      console.error('Error fetching messages action statuses:', error);
      throw error;
    }
  }

  /**
   * Save a draft (create or update)
   * @param {Object} data - { to, cc, bcc, subject, message, replyToMessageId, threadId, attachments, draftId }
   */
  async saveDraft(data) {
    try {
      const { attachments, ...draftData } = data;
      let processedAttachments = undefined;
      if (attachments && attachments.length > 0) {
        processedAttachments = [];
        for (const att of attachments) {
          if (att.gcsPath) {
            processedAttachments.push({ name: att.name, gcsPath: att.gcsPath, mimeType: att.mimeType });
          } else if (att.sourceMessageId && (att.attachmentId || att.url)) {
            processedAttachments.push({
              name: att.name || att.filename,
              mimeType: att.mimeType || att.contentType,
              sourceMessageId: att.sourceMessageId,
              attachmentId: att.attachmentId || att.url,
            });
          } else if (att instanceof File) {
            const formData = new FormData();
            formData.append('file', att);
            const uploadResponse = await apiClient.upload(`/messages/upload-attachment`, formData);
            if (uploadResponse?.success && uploadResponse.data) {
              processedAttachments.push({
                name: uploadResponse.data.name,
                gcsPath: uploadResponse.data.gcsPath,
                mimeType: uploadResponse.data.mimeType
              });
            }
          } else if (att && typeof att === 'object') {
            processedAttachments.push({ ...att, name: att.name || att.filename, mimeType: att.mimeType || att.contentType });
          }
        }
      }

      const method = data.draftId ? 'put' : 'post';
      const url = data.draftId
        ? `${INBOX_BASE_URL}/drafts/${data.draftId}`
        : `${INBOX_BASE_URL}/actions/save-draft`;
      const response = await apiClient[method](url, { ...draftData, attachments: processedAttachments });
      return response;
    } catch (error) {
      console.error('Error saving draft:', error);
      throw error;
    }
  }

  /**
   * List drafts
   * @param {Object} params - { maxResults, pageToken }
   */
  async listDrafts(params = {}) {
    try {
      const response = await apiClient.get(`${INBOX_BASE_URL}/drafts`, { params });
      return response;
    } catch (error) {
      console.error('Error listing drafts:', error);
      throw error;
    }
  }

  /**
   * Get a single draft by ID
   * @param {string} draftId
   */
  async getDraft(draftId) {
    try {
      const response = await apiClient.get(`${INBOX_BASE_URL}/drafts/${draftId}`);
      return response;
    } catch (error) {
      console.error('Error fetching draft:', error);
      throw error;
    }
  }

  /**
   * Delete a draft
   * @param {string} draftId
   */
  async deleteDraft(draftId) {
    try {
      const response = await apiClient.delete(`${INBOX_BASE_URL}/drafts/${draftId}`);
      return response;
    } catch (error) {
      console.error('Error deleting draft:', error);
      throw error;
    }
  }

  /**
   * Send a saved draft
   * @param {string} draftId
   */
  async sendDraft(draftId) {
    try {
      const response = await apiClient.post(`${INBOX_BASE_URL}/drafts/${draftId}/send`, {});
      return response;
    } catch (error) {
      console.error('Error sending draft:', error);
      throw error;
    }
  }

  async disconnectEmail(role = 'personal') {
    try {
      const response = await apiClient.delete(`/email-auth/disconnect`, { params: { role } });
      return response;
    } catch (error) {
      console.error('Error disconnecting email:', error);
      throw error;
    }
  }

  async getConnectionStatus() {
    try {
      const response = await apiClient.get(`/email-auth/status`);
      return response;
    } catch (error) {
      console.error('Error fetching connection status:', error);
      throw error;
    }
  }
  async discoverContacts() {
    try {
      const response = await apiClient.post(`${INBOX_BASE_URL}/actions/discover-contacts`, {});
      return response;
    } catch (error) {
      console.error('Error starting contact discovery:', error);
      throw error;
    }
  }

  async getDiscoveryStatus() {
    try {
      const response = await apiClient.get(`${INBOX_BASE_URL}/discovery-status`);
      return response;
    } catch (error) {
      console.error('Error fetching discovery status:', error);
      return null;
    }
  }

  async listDiscoveryBuckets(options = {}) {
    try {
      const { status, limit = 100, search } = options;
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (limit) params.append('limit', limit);
      if (search) params.append('search', search);

      const response = await apiClient.get(`${INBOX_BASE_URL}/discovery-buckets?${params.toString()}`);
      return response;
    } catch (error) {
      console.error('Error fetching discovery buckets:', error);
      return null;
    }
  }

  async updateBucketStatus(domain, status) {
    try {
      const response = await apiClient.patch(`${INBOX_BASE_URL}/discovery-buckets/${domain}`, { status });
      return response;
    } catch (error) {
      console.error('Error updating bucket status:', error);
      return null;
    }
  }

  async addBucketContact(domain, contactData) {
    try {
      const response = await apiClient.post(`${INBOX_BASE_URL}/discovery-buckets/${domain}/participants`, contactData);
      return response;
    } catch (error) {
      console.error('Error adding bucket contact:', error);
      return null;
    }
  }

  async searchDiscoveryContacts(query = '', limit = 25) {
    try {
      const response = await apiClient.get(`${INBOX_BASE_URL}/discovery-contacts`, {
        params: {
          q: query,
          limit
        }
      });
      return response;
    } catch (error) {
      console.error('Error searching discovery contacts:', error);
      return null;
    }
  }

  /**
   * Download an attachment from an inbox message
   * @param {string} messageId 
   * @param {string} attachmentId 
   * @param {string} filename 
   */
  /**
   * Download an attachment from an inbox message
   * @param {string} messageId 
   * @param {string} attachmentId 
   * @param {string} filename 
   */
  async downloadInboxAttachment(messageId, attachmentId, filename) {
    try {
      const blob = await this.getAttachmentBlob(messageId, attachmentId);

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading inbox attachment:', error);
      throw error;
    }
  }

  /**
   * Get raw attachment blob from an inbox message
   * @param {string} messageId 
   * @param {string} attachmentId 
   * @returns {Promise<Blob>}
   */
  async getAttachmentBlob(messageId, attachmentId) {
    try {
      const blob = await apiClient.getBlobData(`${INBOX_BASE_URL}/messages/${messageId}/attachments/${attachmentId}`, {
        responseType: 'blob'
      });
      return blob;
    } catch (error) {
      console.error('Error fetching attachment blob:', error);
      throw error;
    }
  }


  /**
   * List Gmail labels
   * @returns {Promise<Array>}
   */
  async listLabels() {
    try {
      const response = await apiClient.get(`${INBOX_BASE_URL}/labels`);
      return response;
    } catch (error) {
      console.error('Error fetching labels:', error);
      throw error;
    }
  }

  // --- END NEW INBOX API METHODS ---

  /**
   * List filtered emails by sender list and/or date
   * @param {Object} params - Query parameters: fromList (comma-separated), date
   * @returns {Promise<Array>} Array of filtered email objects
   */
  async listEmailsFiltered(params = {}) {
    try {
      const response = await apiClient.get(`${BASE_URL}/filtered`, { params });
      return response
    } catch (error) {
      console.error('Error fetching filtered emails:', error);
      throw error;
    }
  }

  /**
   * Send an email
   * @param {Object} data - { to, subject, message }
   * @returns {Promise<Object>} Response
   */
  async sendEmail(data) {
    try {
      const response = await apiClient.post(`${BASE_URL}/send-new`, data);
      return response;
    } catch (error) {
      console.error('Error sending email:', error);
      throw error;
    }
  }

  async runEmailAgentOnce(lookbackMinutes) {
    try {
      const payload = {};
      if (lookbackMinutes && Number(lookbackMinutes) > 0) {
        payload.lookbackMinutes = Number(lookbackMinutes);
      }
      const response = await apiClient.post(`${EMAIL_AGENT_BASE_URL}/poller/run-once`, payload);
      return response;
    } catch (error) {
      console.error('Error running email agent poller once:', error);
      throw error;
    }
  }

  async listEmailAgentCandidates(params = {}) {
    try {
      const response = await apiClient.get(`${EMAIL_AGENT_BASE_URL}/candidates`, { params });
      return response;
    } catch (error) {
      console.error('Error fetching email agent candidates:', error);
      throw error;
    }
  }

  async listEmailAgentRuns(params = {}) {
    try {
      const response = await apiClient.get(`${EMAIL_AGENT_BASE_URL}/runs`, { params });
      return response;
    } catch (error) {
      console.error('Error fetching email agent runs:', error);
      throw error;
    }
  }

  async approveEmailAgentCandidate(messageId, mappedClaim) {
    try {
      const payload = mappedClaim && typeof mappedClaim === 'object'
        ? { mappedClaim }
        : {};
      const response = await apiClient.post(`${EMAIL_AGENT_BASE_URL}/candidates/${messageId}/approve`, payload);
      return response;
    } catch (error) {
      console.error('Error approving email agent candidate:', error);
      throw error;
    }
  }

  async rejectEmailAgentCandidate(messageId, reason) {
    try {
      const payload = reason ? { reason } : {};
      const response = await apiClient.post(`${EMAIL_AGENT_BASE_URL}/candidates/${messageId}/reject`, payload);
      return response;
    } catch (error) {
      console.error('Error rejecting email agent candidate:', error);
      throw error;
    }
  }

  async getEmailAgentRun(messageId) {
    try {
      const response = await apiClient.get(`${EMAIL_AGENT_BASE_URL}/runs/${messageId}`);
      return response;
    } catch (error) {
      console.error('Error fetching email agent run details:', error);
      throw error;
    }
  }


  /**
   * Download an attachment
   * @param {string} messageId - The email message ID
   * @param {string} attachmentId - The attachment ID
   * @param {string} filename - The filename to save as
   */
  async downloadAttachment(messageId, attachmentId, filename) {
    try {
      const blob = await apiClient.getBlobData(`${BASE_URL}/${messageId}/attachments/${attachmentId}`, {
        responseType: 'blob'
      });

      // Create object URL and trigger download
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      throw error;
    }
  }
}

export const emailService = new EmailService();
